import { Measurements } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, renderIf } from '../utility/lit-entity';
import { formatDateTimeShort } from '../utility/dates';
import { sharedStyles } from '../styles/shared-styles';
import '../common/bn-action';
import '../common/bn-card';
import '../common/bn-property';
import '../common/bn-modal-button';
import '../common/bn-surface';
import '../users/props/user-name';
import '../work-orders/actions/hold-button';
import '../work-orders/actions/ready-button';
import '../work-orders/parts/work-order-view-modal';
import '../work-orders/actions/work-order-notes';
import '../bins/parts/bin-path-name';
import '../devices/props/device-serial-number';
import '../organizations/props/organization-name';
import '../organizations/parts/organization-ancestors-bread-crumbs';

class WorkOrderAdminView extends LitEntity {
  static get properties() {
    return {
      locationEntity: { type: Object, attribute: false },
    };
  }

  static get styles () {
    return [sharedStyles, css`
      .content {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        padding-bottom: 5rem;
      }

      .properties {
        margin: 2em;
        flex-grow: 1;
      }

      .properties .notes {
        white-space: pre-wrap;
        display: block;
      }

      .properties .actions {
        margin-top: 1em;
      }

      .links {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        align-items: stretch;
      }

      .history {
        flex-grow: 3;
      }

      .history user-name,
      .history .timestamp {
        display: block;
        font-size: .8em;
      }

      .id {
        padding: 1em;
        display: flex;
        flex-wrap: wrap;
      }

      .fields bn-property {
        --align-value: right;
      }
      .fields .group {
        width: fit-content;
      }
      .fields .group + .group {
        margin-top: 1em;
        padding-top: 1em;
        border-top: 1px solid var(--gray-40);
      }
    `];
  }

  renderEntity() {
    const {
      requestType,
      state,
      preDiagnosis,
      fieldDiagnosis,
      fieldAction,
      scheduledFor,
      createdAt,
      completedAt,
      notes,
    } = this.entity.properties;

    const binHref = this.getHref('https://api.binsentry.com/rel/bin');

    const devices = this.entity.getSubEntities('https://api.binsentry.com/rel/device');

    return html`
      <lit-entity .href=${this.getHref('https://api.binsentry.com/rel/work-order-location')} @update=${this.onLocationUpdate}></lit-entity>

      <div class="id">
        <label>Work Order ID:</label>
        <input type="text" disabled value="${this.href}">
      </div>

      <div class="content">
        <div class="box properties">
          <bn-property label="Request Type"><strong>${requestType}</strong></bn-property>
          <bn-property label="State">${state}</bn-property>
          <bn-property label="Notes"><span class="notes">${notes}</span></bn-property>
          <bn-property label="Pre-Diagnosis">${preDiagnosis}</bn-property>
          <bn-property label="Field Diagnosis">${fieldDiagnosis}</bn-property>
          <bn-property label="Field Action">${fieldAction}</bn-property>
          <bn-property label="Created">${createdAt && formatDateTimeShort(createdAt)}</bn-property>
          <bn-property label="Scheduled For">${scheduledFor && formatDateTimeShort(scheduledFor)}</bn-property>
          <bn-property label="Completed">${completedAt && formatDateTimeShort(completedAt)}</bn-property>

          <div class="actions">
            <bn-modal-button button-class="button--contained" full-width>
              <span slot="text">View Work Order</span>
              <work-order-view-modal .href="${this.href}" slot="modal"></work-order-view-modal>
            </bn-modal-button>
          </div>
        </div>

        <div class="box links">
          ${renderIf(binHref, () => html`
            <bn-surface class="box link bin">
              <h3>Bin</h3>
              <bin-path-name
                .href="${binHref}"
                ancestorMinIndex="0"
                linkPath="/customers"
                query-param="organizationId"
                bin-link-path="/bins/show/overview?binId=${encodeURIComponent(binHref)}"
              ></bin-path-name>
            </bn-surface>
          `)}

          ${devices.map((device) => html`
            <bn-surface>
              <h3>Device</h3>
              <bn-property label="Serial Number">
                <a href="/devices/show?deviceId=${encodeURIComponent(device.href)}" class="box link device">
                  <device-serial-number .href="${device.href}"></device-serial-number>
                </a>
              </bn-property>
            </bn-surface>
          `)}

          ${this.renderWorkOrderLocation()}
        </div>

        ${this.renderHistory()}
      </div>
    `;
  }

  renderWorkOrderLocation() {
    if (!this.locationEntity) {
      return html``;
    }

    const { state, scheduledFor } = this.locationEntity.properties;
    const servicePartnerLink = this.locationEntity.getSubEntityByRel('https://api.binsentry.com/rel/service-partner');
    const installerLink = this.locationEntity.getSubEntityByRel('https://api.binsentry.com/rel/installer');

    return html`
      <bn-surface>
        <h3>Work Order Location</h3>
        <bn-property label="State">${state}</bn-property>
        ${renderIf(servicePartnerLink, () => html`
          <bn-property  label="Service Partner">
            <organization-ancestors-bread-crumbs .href="${servicePartnerLink.href}"></organization-ancestors-bread-crumbs>
          </bn-property>
        `)}
        ${renderIf(installerLink, () => html`
          <bn-property  label="Scheduled For">
            ${formatDateTimeShort(scheduledFor)}
          </bn-property>
          <bn-property  label="Installer">
            <a href="/scheduler/user?userId=${encodeURIComponent(installerLink.href)}">
              <organization-name .href="${installerLink.href}"></organization-name>
            </a>
          </bn-property>
        `)}
      </bn-surface>
    `;
  }

  renderHistory() {
    const history = this.entity.properties.history.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    return html`
      <bn-surface class="box history">
        <h3>History</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            ${history.map(({ userId, timestamp, action, fields, state }) => html`
              <tr>
                <td>
                  ${action || 'update-state'}
                  <user-name .href=${userId}></user-name>
                  <span class="timestamp">${formatDateTimeShort(timestamp)}</span>
                </td>
                <td class="fields">${this.renderFields(fields || { state })}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </bn-surface>
    `;
  }

  renderFields(fields) {
    return Object.entries(fields).map(([key, val]) => {
      if (key === 'installationRequestId') {
        const href = `/work-order-admin/show?workOrderId=${encodeURIComponent(val)}`;
        return html`
          <div class="group">
            <bn-property label="${key}">
              <a href="${href}">${val}</a>
            </bn-property>
          </div>
        `;
      }
      if (key === 'binId') {
        const href = `/bins/show/overview?binId=${encodeURIComponent(val)}`;
        return html`
          <div class="group">
            <bn-property label="${key}">
              <a href="${href}">${val}</a>
            </bn-property>
          </div>
        `;
      }
      if (key === 'device') {
        return this.renderDevice(val);
      }
      if (key === 'dimensions') {
        return this.renderDimensions(val);
      }
      if (key === 'location') {
        return this.renderLocation(val);
      }
      if (typeof val === 'object') {
        return html`
          <label>${key}</label>
          <div>
            <div class="group">
              ${this.renderFields(val)}
            </div>
          </div>
        `;
      }
      return html`
        <div class="group">
          <bn-property label="${key}">${val}</bn-property>
        </div>
      `;
    });
  }

  renderDevice(device) {
    const { serialNumber } = device;
    return html`
      <div class="group">
        <bn-property label="Serial Number">${serialNumber}</bn-property>
      </div>
    `;
  }

  renderLocation(location) {
    const { address, latitude, longitude } = location;
    return html`
      <div class="group">
        <bn-property label="Address">${address}</bn-property>
        <bn-property label="Latitude">${latitude}</bn-property>
        <bn-property label="Longitude">${longitude}</bn-property>
      </div>
    `;
  }

  renderDimensions(dimensions) {
    const { top, body, hopper, ringCount, profile, lipHeight, deviceDepth } = dimensions;
    const measurement = Measurements.get('dimension');
    return html`
      <div class="group">
        <bn-property label="Profile">${profile}</bn-property>
        <bn-property label="Ring Count">${ringCount}</bn-property>
        <bn-property label="Lip Height">${measurement.html({ dimension: lipHeight })}</bn-property>
        <bn-property label="Device Depth">${measurement.html({ dimension: deviceDepth })}</bn-property>

        <bn-property label="Top Angle">${top.angle} <span class="unit">degrees</span></bn-property>
        <bn-property label="Top Height">${measurement.html({ dimension: top.height })}</bn-property>
        <bn-property label="Top Radius">${measurement.html({ dimension: top.truncatedRadius })}</bn-property>

        <bn-property label="Body Height">${measurement.html({ dimension: body.height })}</bn-property>
        <bn-property label="Body Radius">${measurement.html({ dimension: body.radius })}</bn-property>

        <bn-property label="Hopper Angle">${hopper.angle} <span class="unit">degrees</span></bn-property>
        <bn-property label="Hopper Height">${measurement.html({ dimension: hopper.height })}</bn-property>
        <bn-property label="Hopper Radius">${measurement.html({ dimension: hopper.truncatedRadius })}</bn-property>
      </div>
    `;
  }

  onLocationUpdate(e) {
    this.locationEntity = e.detail.entity;
  }
}

customElements.define('work-order-admin-view', WorkOrderAdminView);
